import { forwardRef, useCallback } from "react";
import Image from "next/image";
import { cmsImageLoader } from "src/utils/cms-image-loader";
import { Skeleton } from "src/atoms/Skeleton";
import { useImageLoaded } from "src/hooks/use-image-loaded";

type BrandCardImageProps = {
  src: string;
  alt?: string;
  isBottomRadius: boolean;
  noRadius?: boolean;
  hovered?: boolean;
  isGated?: boolean;
  isMobile?: boolean;
};

const getBorderRadius = (isBottomRadius: boolean, noRadius?: boolean): string =>
  noRadius ? "0" : isBottomRadius ? "8px" : "8px 8px 0 0";

const BrandCardImage = forwardRef<HTMLImageElement, BrandCardImageProps>(
  (
    { src, alt = "", isBottomRadius, noRadius, hovered, isGated, isMobile },
    ref
  ) => {
    const { imgRef, loaded, onLoad } = useImageLoaded();
    const borderRadius = getBorderRadius(isBottomRadius, noRadius);

    // Merge the load-tracking ref with the forwarded ref (used by the card's
    // image-error detection) so the underlying <img> feeds both.
    const setRefs = useCallback(
      (node: HTMLImageElement | null) => {
        imgRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref, imgRef]
    );

    return (
      <>
        {!loaded && (
          <Skeleton style={{ position: "absolute", inset: 0, borderRadius }} />
        )}
        <Image
          ref={setRefs}
          loader={cmsImageLoader}
          src={src}
          alt={alt}
          fill
          sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
          onLoad={onLoad}
          style={{
            objectFit: "cover",
            objectPosition: "top",
            borderRadius,
            filter: !isMobile && isGated && hovered ? "blur(8px)" : undefined,
            opacity: loaded ? 1 : 0,
            transition: "opacity 300ms ease",
          }}
        />
      </>
    );
  }
);

BrandCardImage.displayName = "BrandCardImage";

export { BrandCardImage };
